import React from 'react';
import { APITypes, MedicalDataTypes } from '../../../../primary-record-types';
import { getIntl } from '../../../../utils/intl';
import { useNestedNavigation } from '../../../../utils/appNavigationNames';
import useCareTeamMember from '../../../../hooks/api/useCareTeamMember';
import { getCareTeamMemberDescriptorDisplay } from '../../../../utils/careTeamUtils';
import BaseSearchResult from './BaseSearchResult';
import useMedicalProfileFiles from '../../../../hooks/api/useMedicalProfileFiles';
import useNotes from '../../../../hooks/api/useNotes';
import { IconName } from '../../../v2BaseComponents/Icon';
import { useResourceTitle } from '../../../../utils/resourceUtils/getResourceTitle';
import useMedicalProfileResource from '../../../../hooks/api/useMedicalProfileResource';
import { FileArtifactSearchResults } from '../../../../hooks/api/useMedicalProfileSearch';
import { resourceTypeGuards } from '../../../../utils/medicalDataUtils';

const intl = getIntl('search-result', {
    page: '{page}',
    pageWithTotal: '{page}/{totalPages}',
    audioTime: '{startTime}s - {endTime}s',
});

export type SearchResultOwnProps = {
    searchResult: APITypes.chatService.GeneralMedicalProfileSearchResult;
    onSelectedResult: () => void;
    medicalProfileId: string;
    searchText: string;
};

export type SearchResultProps = SearchResultOwnProps;

const msToTime = (duration: number) => {
    const seconds = Math.floor((duration / 1000) % 60);
    const minutes = Math.floor((duration / (1000 * 60)) % 60);
    const hours = Math.floor((duration / (1000 * 60 * 60)) % 24);

    const hoursString = hours < 10 ? '0' + hours : hours;
    const minutesString = minutes < 10 ? '0' + minutes : minutes;
    const secondsString = seconds < 10 ? '0' + seconds : seconds;

    return hours > 0 ? `${hoursString}:${minutesString}:${secondsString}` : `${minutesString}:${secondsString}`;
};

const CareTeamMemberSearchResult: React.FC<{
    medicalProfileId: string;
    careTeamMemberId: string;
    onSelectedResult: () => void;
    searchText: string;
}> = ({ medicalProfileId, careTeamMemberId, onSelectedResult, searchText }) => {
    const { nestedProfileNavigation } = useNestedNavigation();
    const { isLoadingCareTeamMember, careTeamMember } = useCareTeamMember({
        medicalProfileId,
        careTeamMemberId,
    });

    const getName = () => {
        if (!careTeamMember) return null;

        return getCareTeamMemberDescriptorDisplay(careTeamMember);
    };

    return (
        <BaseSearchResult
            text={getName()}
            isLoading={isLoadingCareTeamMember}
            onPress={() => {
                onSelectedResult();
                nestedProfileNavigation('care-team-member', { profileId: medicalProfileId, careTeamMemberId });
            }}
            iconName="CareTeamFilled"
            searchText={searchText}
        />
    );
};

const FileArtifactSearchResult: React.FC<{
    medicalProfileId: string;
    artifact: FileArtifactSearchResults;
    onSelectedResult: () => void;
    searchText: string;
}> = ({ artifact, onSelectedResult, searchText }) => {
    const { nestedProfileNavigation } = useNestedNavigation();
    const { file, isLoadingFile } = useMedicalProfileFiles({
        medicalProfileFileId: artifact.medicalProfileFileId,
        medicalProfileId: artifact.medicalProfileId,
    });

    const getSecondaryText = () => {
        if (isLoadingFile || !file) return;

        if ((artifact.type === 'page' || artifact.type === 'image') && file.pageCount) {
            return intl('pageWithTotal', {
                page: artifact.page,
                totalPages: file.pageCount,
            });
        } else if (artifact.type === 'audio') {
            return intl('audioTime', {
                startTime: msToTime(artifact.startTime),
                endTime: msToTime(artifact.endTime),
            });
        }
    };

    const getSubText = () => {
        if (artifact.type === 'image') return artifact.texts?.[0]?.text;
        if (artifact.type === 'audio') return artifact.text;
    };

    const getBBArgs = (): {
        bbTop?: string;
        bbLeft?: string;
        bbWidth?: string;
        bbHeight?: string;
    } => {
        if (artifact.type !== 'image' || !file) return {};

        // round to 4 decimals and convert to string
        const roundAndString = (num: number | undefined) => {
            if (num === undefined) return undefined;

            return String(num.toFixed(4));
        };

        const boundingBox = artifact.texts?.[0]?.boundingBox;
        if (!boundingBox) return {};

        return {
            bbTop: roundAndString(boundingBox.top),
            bbHeight: roundAndString(boundingBox.height),
            bbLeft: roundAndString(boundingBox.left),
            bbWidth: roundAndString(boundingBox.width),
        };
    };

    return (
        <BaseSearchResult
            text={file?.fileName || ''}
            secondaryHeader={getSecondaryText()}
            isLoading={isLoadingFile}
            onPress={() => {
                onSelectedResult();
                nestedProfileNavigation('file', {
                    fileId: artifact.medicalProfileFileId,
                    startTime: artifact.type === 'audio' ? artifact.startTime.toString() : undefined,
                    page: artifact.page.toString(),
                    profileId: artifact.medicalProfileId,
                    ...getBBArgs(),
                });
            }}
            placeholderLength={40}
            iconName="FilesFilled"
            subText={getSubText()}
            searchText={searchText}
        />
    );
};

const NoteSearchResult: React.FC<{
    noteId: string;
    onSelectedResult: () => void;
    searchText: string;
}> = ({ noteId, onSelectedResult, searchText }) => {
    const { nestedProfileNavigation } = useNestedNavigation();
    const { note, isLoadingNote } = useNotes({
        noteId,
    });

    const getName = () => {
        if (isLoadingNote || !note) return null;

        return note.title;
    };

    return (
        <BaseSearchResult
            text={getName()}
            isLoading={isLoadingNote}
            onPress={() => {
                onSelectedResult();
                if (note && note.medicalProfileId) {
                    nestedProfileNavigation('note', {
                        noteId: note.noteId,
                        noteEntityType: note.entityType,
                        profileId: note.medicalProfileId,
                    });
                }
            }}
            iconName="NotesFilled"
            subText={note?.plainTextContent}
            searchText={searchText}
        />
    );
};

const resourceCategoryIconMap: Record<MedicalDataTypes.PrimaryRecordResourceCategory, IconName> = {
    allergy: 'AllergiesFilled',
    visit: 'VisitsFilled',
    medication: 'MedicationsFilled',
    condition: 'ConditionsFilled',
    lab: 'LabsFilled',
    procedure: 'ProceduresFilled',
    vaccination: 'VaccinationsFilled',
    imaging: 'Imaging',
};

const ResourceSearchResult: React.FC<{
    medicalProfileId: string;
    resourceId: string;
    resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
    onSelectedResult: () => void;
    searchText: string;
}> = ({ medicalProfileId, resourceCategory, resourceId, onSelectedResult, searchText }) => {
    const { nestedProfileNavigation } = useNestedNavigation();
    const { isLoading, resource } = useMedicalProfileResource({
        medicalProfileId: medicalProfileId,
        resourceCategory: resourceCategory,
        resourceId: resourceId,
    });

    const resourceTitle = useResourceTitle(resource, medicalProfileId);

    const getSubText = () => {
        if (resource && resourceTypeGuards.isVisit(resource) && resource.clinicalImpressions) {
            return resource.clinicalImpressions
                .map(({ title, summary }) => `${title}: ${summary}`)
                .join(', ')
                .replaceAll(/[\r\n]+/g, '');
        }
    };

    return (
        <BaseSearchResult
            text={resourceTitle}
            isLoading={isLoading}
            onPress={() => {
                onSelectedResult();
                if (resource.type !== 'lab')
                    nestedProfileNavigation('data-details', {
                        resourceId: resourceId,
                        category: resource.type,
                        profileId: medicalProfileId,
                    });
                else
                    nestedProfileNavigation('lab-history', {
                        profileId: medicalProfileId,
                        labCode: resource.labType.code,
                    });
            }}
            iconName={resourceCategoryIconMap[resourceCategory]}
            searchText={searchText}
            subText={getSubText()}
        />
    );
};

const SearchResult: React.FC<SearchResultProps> = (props) => {
    const { searchResult, medicalProfileId } = props;

    if (searchResult.category === 'care-team') {
        return (
            <CareTeamMemberSearchResult
                medicalProfileId={medicalProfileId}
                careTeamMemberId={searchResult.id}
                onSelectedResult={props.onSelectedResult}
                searchText={props.searchText}
            />
        );
    } else if (searchResult.category === 'file-artifact') {
        return (
            <FileArtifactSearchResult
                medicalProfileId={medicalProfileId}
                artifact={searchResult}
                onSelectedResult={props.onSelectedResult}
                searchText={props.searchText}
            />
        );
    } else if (searchResult.category === 'note') {
        return (
            <NoteSearchResult
                noteId={searchResult.id}
                onSelectedResult={props.onSelectedResult}
                searchText={props.searchText}
            />
        );
    } else {
        return (
            <ResourceSearchResult
                medicalProfileId={medicalProfileId}
                resourceId={searchResult.id}
                resourceCategory={searchResult.category}
                onSelectedResult={props.onSelectedResult}
                searchText={props.searchText}
            />
        );
    }
};

export default React.memo(SearchResult);
